import React from 'react';

import { Tabs as MuiTabs } from '@material-ui/core';

import type { ComponentPropsWithRef } from '@coursera/cds-common';

import getTabsCss, { classes } from './getTabsCss';
import type { Props as TabProps } from './Tab';
import { getTabId, getPanelId, useTabContext } from './TabContext';

export type Props = {
  /**
   * Callback fired when the selected tab changes.
   * @param value Value for the selected tab.
   */
  onChange?: (value: string) => void;
  /**
   * The variants of tabs.
   * @default primary
   */
  variant?: 'primary' | 'section';
  /**
   * Provide a label for the Tabs.
   * Use `aria-labelledby` instead if you have a visible label.
   */
  'aria-label'?: string;
  /**
   * Use when you have a visible label.
   */
  'aria-labelledby'?: string;
  /**
   * Whether tabs are activated manually on focus or automatically.
   * @default manual
   */
  keyboardActivation?: 'manual' | 'automatic';
  /**
   * A list of <Tab /> elements.
   */
  children: React.ReactElement<TabProps>[];
} & Omit<ComponentPropsWithRef<'button'>, 'onChange'>;

/**
 * Tabs is a navigation component that allows users to navigate among a set of relevant content sections.
 *
 * See [Tabs](__storybookUrl__/components-navigation-tabs--default)
 */
const Tabs = React.forwardRef(function Tabs(
  props: Props,
  ref: React.Ref<HTMLButtonElement>
): React.ReactElement<Props> {
  const context = useTabContext();

  if (!context) {
    throw new TypeError('No TabContext provided');
  }

  /* eslint-disable-next-line @typescript-eslint/no-unused-vars */
  const {
    children: childrenProp,
    onChange,
    variant,
    keyboardActivation,
    ...rest
  } = props;

  const children = React.Children.map(childrenProp, (child) =>
    // @ts-expect-error React 18 migration
    React.cloneElement(child, {
      variant,
      // @ts-expect-error React 18 migration
      id: getTabId(context, child.props.value),
      // @ts-expect-error React 18 migration
      'aria-controls': getPanelId(context, child.props.value),
    })
  );

  return (
    <MuiTabs
      {...rest}
      ref={ref}
      classes={classes}
      css={getTabsCss}
      data-testid="tabs-root"
      scrollButtons="off"
      selectionFollowsFocus={keyboardActivation !== 'manual'}
      value={context.value}
      variant="scrollable"
      onChange={(_, value) => onChange?.(value)}
    >
      {children}
    </MuiTabs>
  );
});

Tabs.defaultProps = {
  variant: 'primary',
  keyboardActivation: 'manual',
};

export default Tabs;
